// src/utils/asinMonthlyResultsFormatters.ts

/**
 * Utility functions for formatting ASIN monthly results data
 * Implements the specific formatting requirements for each metric type
 */

/**
 * Safely parse a value to a number, handling null/undefined/empty values
 */
const parseSafeFloat = (value: any): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const cleanedValue = String(value).replace(/[$,%]/g, '').replace(/,/g, '');
  const num = parseFloat(cleanedValue);
  return isNaN(num) ? null : num;
};

/**
 * Format currency values - round to nearest whole number, no decimals
 * Example: 42171.33 → "$42,171"
 */
export const formatCurrency = (value: any): string => {
  const num = parseSafeFloat(value);
  if (num === null) return '-';
  return `$${Math.round(num).toLocaleString('en-US')}`;
};

/**
 * Format currency values with cents (for PPC Cost Per Click)
 * Example: 1.58 → "$1.58"
 */
export const formatCurrencyWithCents = (value: any): string => {
  const num = parseSafeFloat(value);
  if (num === null) return '-';
  return `$${num.toFixed(2)}`;
};

/**
 * Format decimal values as percentages with one decimal place
 * Example: 0.294 → "29.4%"
 */
export const formatPercentage = (value: any): string => {
  const num = parseSafeFloat(value);
  if (num === null) return '-';
  return `${(num * 100).toFixed(1)}%`;
};

/**
 * Format whole numbers with thousands separators, no decimals
 * Example: 15420.7 → "15,421"
 */
export const formatWholeNumber = (value: any): string => {
  const num = parseSafeFloat(value);
  if (num === null) return '-';
  return Math.round(num).toLocaleString('en-US');
};

/**
 * Formatter functions mapped by type
 */
export const formatters = {
  currency: formatCurrency,
  currencyWithCents: formatCurrencyWithCents,
  percentage: formatPercentage,
  wholeNumber: formatWholeNumber,
} as const;

/**
 * Format a value based on the specified formatter type
 */
export const formatValue = (
  value: any,
  formatterType: keyof typeof formatters
): string => {
  return formatters[formatterType](value);
};

/**
 * Get the raw numeric value for calculations/comparisons
 */
export const getRawValue = (value: any): number | null => {
  return parseSafeFloat(value);
};